import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  FilterBarChart,
  Input,
  Label,
  LoadingSkeleton,
  PageHeader,
  Pagination,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ResponsiveTabsList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsTrigger,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarChart3,
  Check,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Pencil,
  Plus,
  X,
} from 'lucide-react'
import { useState } from 'react'
import {
  DIREKT,
  usePlatformApi,
  type PlattformNutzer,
  type VerwaltungsRolle,
} from './api'
import { AuditLogTab } from './audit-log-tab'
import { RollenTab } from './rollen-tab'
import {
  groupRoles,
  message,
  moduleTitle,
  PersonPicker,
  roleTitle,
  useDebounced,
} from './permission-helpers'

export interface PermissionTemplate {
  id: string
  name: string
  version: string
  roles: string[]
}
interface Overview {
  people: number
  templates: number
  directAssignments: number
  modules: { key: string; count: number }[]
  sources: { key: string; count: number }[]
}
interface PeoplePage {
  items: PlattformNutzer[]
  total: number
  page: number
  pageSize: number
}
interface PersonDraft {
  person: PlattformNutzer
  before: string[]
  after: string[]
}
interface TemplateDraft extends PermissionTemplate {
  delete?: boolean
  isNew?: boolean
}
interface MatrixRow {
  id: string
  name: string
  email?: string
  roles: string[]
  inherited: string[]
  changed: boolean
  blocked?: boolean
}
const EMPTY_VERSION = '00000000-0000-0000-0000-000000000000'
const directRoles = (person: PlattformNutzer) => [
  ...new Set(
    person.roles.filter((grant) => grant.source === DIREKT).map((grant) => grant.role),
  ),
]
const sameRoles = (a: string[], b: string[]) =>
  a.length === b.length && a.every((role) => b.includes(role))

export function PermissionWorkspace() {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [tab, setTab] = useState('people')
  const [search, setSearch] = useState('')
  const term = useDebounced(search)
  const [module, setModule] = useState('')
  const [source, setSource] = useState('')
  const [templateSearch, setTemplateSearch] = useState('')
  const [analysisOpen, setAnalysisOpen] = useState(false)
  const [information, setInformation] = useState<'catalog' | 'audit' | null>(null)
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, PersonDraft>>({})
  const [templateDrafts, setTemplateDrafts] = useState<Record<string, TemplateDraft>>({})
  const [review, setReview] = useState(false)
  const [discard, setDiscard] = useState(false)
  const [addPerson, setAddPerson] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState<string | null>(null)
  const [person, setPerson] = useState<PlattformNutzer | null>(null)
  const [applyTo, setApplyTo] = useState<PlattformNutzer | null>(null)
  const [chosenTemplate, setChosenTemplate] = useState('')
  const [templateDetails, setTemplateDetails] = useState<PermissionTemplate | null>(null)
  const catalog = useQuery({
    queryKey: ['verwaltung', 'roles'],
    queryFn: ({ signal }) => api.get<VerwaltungsRolle[]>('/roles', { signal }),
  })
  const templates = useQuery({
    queryKey: ['verwaltung', 'permission-templates'],
    queryFn: ({ signal }) =>
      api.get<PermissionTemplate[]>('/permission-templates', { signal }),
  })
  const people = useQuery({
    queryKey: ['verwaltung', 'permissions', 'people', term, module, source, page],
    queryFn: ({ signal }) =>
      api.get<PeoplePage>('/permissions/people', {
        signal,
        query: { search: term, module, source, page },
      }),
  })
  const overview = useQuery({
    queryKey: ['verwaltung', 'permissions', 'overview'],
    enabled: analysisOpen,
    queryFn: ({ signal }) => api.get<Overview>('/permissions/overview', { signal }),
  })
  const roles = catalog.data ?? []
  const modules = [...new Set(roles.map((role) => role.moduleId))].sort((a, b) =>
    moduleTitle(a).localeCompare(moduleTitle(b), 'de'),
  )
  // A module filter selects people, not columns: the complete access of each matching person stays visible.
  const changedPeople = Object.values(drafts)
  const changedTemplates = Object.values(templateDrafts)
  const dirty = changedPeople.length + changedTemplates.length > 0
  const ready =
    !catalog.isPending && !catalog.isError && !templates.isPending && !templates.isError
  function setPersonRoles(target: PlattformNutzer, after: string[]) {
    const before = drafts[target.id]?.before ?? directRoles(target)
    setDrafts((old) => {
      const next = { ...old }
      if (sameRoles(before, after)) delete next[target.id]
      else
        next[target.id] = {
          person: drafts[target.id]?.person ?? target,
          before,
          after: [...new Set(after)],
        }
      return next
    })
  }
  function setRowRoles(id: string, after: string[]) {
    if (tab === 'people') {
      const target = people.data?.items.find((item) => item.id === id)
      if (target) setPersonRoles(target, after)
    } else {
      const target = templateDrafts[id] ?? templates.data?.find((item) => item.id === id)
      if (target)
        setTemplateDrafts((old) => {
          const next = { ...old, [id]: { ...target, roles: after } }
          const original = templates.data?.find((item) => item.id === id)
          if (
            original &&
            sameRoles(original.roles, after) &&
            original.name === target.name
          )
            delete next[id]
          return next
        })
    }
  }
  const save = useMutation({
    mutationFn: () =>
      tab === 'people'
        ? api.post('/permissions/matrix', {
            people: changedPeople.map((item) => ({
              userId: item.person.id,
              before: item.before,
              after: item.after,
            })),
          })
        : api.post('/permission-templates', {
            templates: changedTemplates.map((item) => ({
              id: item.isNew ? null : item.id,
              name: item.name,
              version: item.version,
              roles: item.roles,
              delete: item.delete ?? false,
            })),
          }),
    onSuccess: async () => {
      setDrafts({})
      setTemplateDrafts({})
      setReview(false)
      setEditing(false)
      await client.invalidateQueries({ queryKey: ['verwaltung'] })
      toast.success(
        tab === 'people' ? 'Berechtigungen gespeichert' : 'Rechtevorlagen gespeichert',
      )
    },
    onError: (error) => toast.error(message(error)),
  })
  function endEditing() {
    if (dirty) setDiscard(true)
    else setEditing(false)
  }
  const personRows: MatrixRow[] = (people.data?.items ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    email: item.email,
    roles: drafts[item.id]?.after ?? directRoles(item),
    inherited: [
      ...new Set(
        item.roles.filter((role) => role.source !== DIREKT).map((role) => role.role),
      ),
    ],
    changed: Boolean(drafts[item.id]),
    blocked: item.isBlocked,
  }))
  const allTemplates = [
    ...(templates.data ?? []).map((item) => templateDrafts[item.id] ?? item),
    ...changedTemplates.filter((item) => item.isNew),
  ]
  const templateRows: MatrixRow[] = allTemplates
    .filter(
      (item) =>
        !('delete' in item && item.delete) &&
        item.name
          .toLocaleLowerCase('de')
          .includes(templateSearch.toLocaleLowerCase('de')),
    )
    .map((item) => ({
      id: item.id,
      name: item.name,
      roles: item.roles,
      inherited: [],
      changed: Boolean(templateDrafts[item.id]),
    }))
  const currentTemplate = allTemplates.find((item) => item.id === chosenTemplate)
  return (
    <div className="space-y-4">
      <PageHeader title="Module & Rechte" className="pb-0" />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          if (!editing) setTab(value)
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <ResponsiveTabsList aria-label="Berechtigungsverwaltung">
            <TabsTrigger value="people" disabled={editing && tab !== 'people'}>
              Personen
            </TabsTrigger>
            <TabsTrigger value="templates" disabled={editing && tab !== 'templates'}>
              Rechtevorlagen
            </TabsTrigger>
          </ResponsiveTabsList>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Weitere Informationen">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setInformation('catalog')}>
                Rollenkatalog
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setInformation('audit')}>
                Änderungsprotokoll
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <TabsContent value="people" className="space-y-4 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Personen durchsuchen"
              placeholder="Name oder E-Mail suchen"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="sm:max-w-sm"
            />
            <Select
              value={module || 'all'}
              onValueChange={(value) => {
                setModule(value === 'all' ? '' : value)
                setPage(1)
              }}
            >
              <SelectTrigger
                aria-label="Personen mit Modulzugriff filtern"
                className="w-full sm:w-56"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Module</SelectItem>
                {modules.map((id) => (
                  <SelectItem key={id} value={id}>
                    {moduleTitle(id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              disabled={editing}
              onClick={() => setAddPerson(true)}
            >
              <Plus className="size-4" />
              Person hinzufügen
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-auto text-sm text-muted-foreground">
              {people.data
                ? `${people.data.total} ${people.data.total === 1 ? 'Person' : 'Personen'}`
                : 'Personen werden geladen …'}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              aria-expanded={analysisOpen}
              aria-controls="permission-analysis"
              onClick={() => setAnalysisOpen((old) => !old)}
            >
              <BarChart3 className="size-4" />
              Grafische Auswertung
              {analysisOpen ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </Button>
            {!editing && (
              <Button
                size="sm"
                disabled={!ready || people.isError || people.isPending}
                onClick={() => setEditing(true)}
              >
                <Pencil className="size-4" />
                Rechte bearbeiten
              </Button>
            )}
          </div>
          {(module || source) && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Nur Personen mit:</span>
              {module && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setModule('')
                    setPage(1)
                  }}
                >
                  {moduleTitle(module)}
                  <X className="size-3" />
                </Button>
              )}
              {source && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSource('')
                    setPage(1)
                  }}
                >
                  {source === DIREKT ? 'direkten Rechten' : 'Rechten aus dem Verzeichnis'}
                  <X className="size-3" />
                </Button>
              )}
            </div>
          )}
          {analysisOpen && (
            <section
              id="permission-analysis"
              aria-label="Berechtigungen im Gesamtbestand"
              className="space-y-3 rounded-lg border p-4"
            >
              <h2 className="font-medium">Grafische Auswertung</h2>
              {overview.isError ? (
                <ErrorState
                  description="Die Auswertung konnte nicht geladen werden."
                  onRetry={() => void overview.refetch()}
                />
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Gesamtbestand: {overview.data?.people ?? '–'} aktive Konten ·{' '}
                    {overview.data?.templates ?? '–'} Rechtevorlagen ·{' '}
                    {overview.data?.directAssignments ?? '–'} direkte Zuweisungen
                  </p>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Personen je Modul</h3>
                      <FilterBarChart
                        label="Nach Modul filtern"
                        itemLabel="Personen"
                        multiSelect={false}
                        maximum={Math.max(
                          1,
                          ...(overview.data?.modules.map((item) => item.count) ?? []),
                        )}
                        selectedKeys={module ? [module] : []}
                        items={(overview.data?.modules ?? []).map((item) => ({
                          key: item.key,
                          label: moduleTitle(item.key),
                          value: item.count,
                        }))}
                        onSelect={(key) => {
                          setModule((old) => (old === key ? '' : key))
                          setPage(1)
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="mb-2 text-sm font-medium">Herkunft der Rechte</h3>
                      <FilterBarChart
                        label="Nach Herkunft filtern"
                        itemLabel="Personen"
                        multiSelect={false}
                        maximum={Math.max(1, overview.data?.people ?? 1)}
                        selectedKeys={source ? [source] : []}
                        items={(overview.data?.sources ?? []).map((item) => ({
                          key: item.key,
                          label:
                            item.key === DIREKT
                              ? 'Direkt vergeben'
                              : 'Aus dem Verzeichnis',
                          value: item.count,
                        }))}
                        onSelect={(key) => {
                          setSource((old) => (old === key ? '' : key))
                          setPage(1)
                        }}
                      />
                      <p className="mt-2 text-xs text-muted-foreground">
                        Eine Person kann Rechte aus mehreren Quellen haben. Die Balken
                        filtern die Personen in der Matrix.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}
          {editing && (
            <EditBar
              count={changedPeople.length}
              pending={save.isPending}
              onCancel={endEditing}
              onReview={() => setReview(true)}
            />
          )}
          {catalog.isError ? (
            <ErrorState
              description="Der Rollenkatalog konnte nicht geladen werden."
              onRetry={() => void catalog.refetch()}
            />
          ) : people.isError ? (
            <ErrorState
              description="Die Personen konnten nicht geladen werden."
              onRetry={() => void people.refetch()}
            />
          ) : catalog.isPending || people.isPending ? (
            <LoadingSkeleton variant="table" rows={6} columns={5} />
          ) : (
            <PermissionMatrix
              label="Personen und Modulrechte"
              firstColumn="Person"
              rows={personRows}
              modules={modules}
              catalog={roles}
              editing={editing}
              onChange={setRowRoles}
              onRow={(id) => {
                const target = people.data?.items.find((item) => item.id === id)
                if (target) setPerson(target)
              }}
              onApply={
                editing
                  ? (id) => {
                      const target = people.data?.items.find((item) => item.id === id)
                      if (target) {
                        setApplyTo(target)
                        setChosenTemplate('')
                      }
                    }
                  : undefined
              }
            />
          )}
          <Pagination
            page={page}
            pageSize={25}
            total={people.data?.total ?? 0}
            onPageChange={setPage}
          />
        </TabsContent>
        <TabsContent value="templates" className="space-y-4 pt-3">
          <p className="text-sm text-muted-foreground">
            Vorlagen befüllen Personenrechte einmalig. Spätere Änderungen an einer Vorlage
            verändern keine bereits vergebenen Rechte.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Rechtevorlagen durchsuchen"
              placeholder="Rechtevorlage suchen"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              className="sm:max-w-sm"
            />
            <Button
              variant="outline"
              disabled={!ready}
              onClick={() => setNewTemplateName('')}
            >
              <Plus className="size-4" />
              Vorlage anlegen
            </Button>
            {!editing && (
              <Button
                disabled={!ready || !templates.data?.length}
                onClick={() => setEditing(true)}
              >
                <Pencil className="size-4" />
                Vorlagen bearbeiten
              </Button>
            )}
          </div>
          {editing && (
            <EditBar
              count={changedTemplates.length}
              pending={save.isPending}
              onCancel={endEditing}
              onReview={() => setReview(true)}
            />
          )}
          {templates.isError || catalog.isError ? (
            <ErrorState
              description="Die Rechtevorlagen konnten nicht geladen werden."
              onRetry={() => {
                void templates.refetch()
                void catalog.refetch()
              }}
            />
          ) : templates.isPending || catalog.isPending ? (
            <LoadingSkeleton variant="table" rows={3} columns={5} />
          ) : (
            <PermissionMatrix
              label="Rechtevorlagen und Modulrechte"
              firstColumn="Rechtevorlage"
              rows={templateRows}
              modules={modules}
              catalog={roles}
              editing={editing}
              onChange={setRowRoles}
              onRow={(id) => {
                const target = allTemplates.find((item) => item.id === id)
                if (target) setTemplateDetails(target)
              }}
            />
          )}
        </TabsContent>
      </Tabs>
      <p className="text-xs text-muted-foreground">
        Häkchen = Zugriff · A = Administration · + = weitere Rollen. Zusätzliche
        Fachrechte werden in der jeweiligen Anwendung vergeben.
      </p>
      {editing && (
        <p className="text-xs text-muted-foreground">
          Änderungen bleiben beim Filtern und Seitenwechsel im Entwurf. Speichern
          übernimmt alle Änderungen dieses Reiters gemeinsam.
        </p>
      )}
      {review && (
        <Dialog
          open
          onOpenChange={(open) => !open && !save.isPending && setReview(false)}
        >
          <DialogContent className="flex max-h-[90svh] flex-col overflow-hidden sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>Änderungen prüfen</DialogTitle>
              <DialogDescription>
                Erst mit dem Speichern werden alle angezeigten Änderungen gemeinsam
                übernommen.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 space-y-4 overflow-y-auto">
              {tab === 'people'
                ? changedPeople.map((item) => (
                    <section key={item.person.id}>
                      <h3 className="font-medium">{item.person.name}</h3>
                      <ChangeList
                        title="Hinzufügen"
                        roles={item.after.filter((role) => !item.before.includes(role))}
                      />
                      <ChangeList
                        title="Entfernen"
                        roles={item.before.filter((role) => !item.after.includes(role))}
                      />
                    </section>
                  ))
                : changedTemplates.map((item) => (
                    <section key={item.id}>
                      <h3 className="font-medium">
                        {item.name}
                        {item.delete ? ' · Löschen' : item.isNew ? ' · Neue Vorlage' : ''}
                      </h3>
                      <ChangeList
                        title="Rollen der Vorlage"
                        roles={item.delete ? [] : item.roles}
                      />
                    </section>
                  ))}
              {changedPeople.some(
                (item) =>
                  item.after.includes('platform.admin') &&
                  !item.before.includes('platform.admin'),
              ) && (
                <Alert>
                  <AlertTitle>Plattformadministration vergeben</AlertTitle>
                  <AlertDescription>
                    Diese Rolle erlaubt die Verwaltung aller Berechtigungen und
                    Plattformdienste.
                  </AlertDescription>
                </Alert>
              )}
              {save.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {message(save.error)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={save.isPending}
                onClick={() => setReview(false)}
              >
                Zurück
              </Button>
              <Button disabled={save.isPending} onClick={() => save.mutate()}>
                Änderungen speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {discard && (
        <Dialog open onOpenChange={setDiscard}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Entwurf verwerfen?</DialogTitle>
              <DialogDescription>
                Alle noch nicht gespeicherten Änderungen werden verworfen.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDiscard(false)}>
                Weiter bearbeiten
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDrafts({})
                  setTemplateDrafts({})
                  setEditing(false)
                  setDiscard(false)
                  void client.invalidateQueries({ queryKey: ['verwaltung'] })
                }}
              >
                Entwurf verwerfen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {person && (
        <Dialog open onOpenChange={(open) => !open && setPerson(null)}>
          <DialogContent className="flex max-h-[90svh] flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>{person.name}</DialogTitle>
              <DialogDescription>{person.email}</DialogDescription>
            </DialogHeader>
            <div className="min-h-0 space-y-3 overflow-y-auto">
              {groupRoles(
                [
                  ...new Set([
                    ...(drafts[person.id]?.after ?? directRoles(person)),
                    ...person.roles
                      .filter((role) => role.source !== DIREKT)
                      .map((role) => role.role),
                  ]),
                ],
                roles,
              ).map(([id, names]) => (
                <section key={id}>
                  <h3 className="font-medium">{moduleTitle(id)}</h3>
                  {names.map((name) => (
                    <p key={name} className="text-sm">
                      {roleTitle(name)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {person.roles
                          .filter((grant) => grant.role === name)
                          .map((grant) =>
                            grant.source === DIREKT ? 'Direkt' : grant.source,
                          )
                          .join(', ') || 'Entwurf'}
                      </span>
                    </p>
                  ))}
                </section>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPerson(null)}>
                Schließen
              </Button>
              <Button
                disabled={!ready}
                onClick={() => {
                  setEditing(true)
                  setApplyTo(person)
                  setPerson(null)
                  setChosenTemplate('')
                }}
              >
                Vorlage anwenden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {applyTo && (
        <Dialog open onOpenChange={(open) => !open && setApplyTo(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vorlage für {applyTo.name}</DialogTitle>
              <DialogDescription>
                Die Rollen werden einmalig zum Entwurf hinzugefügt. Bestehende Rechte
                bleiben erhalten; es entsteht keine Gruppenbindung.
              </DialogDescription>
            </DialogHeader>
            <Select value={chosenTemplate} onValueChange={setChosenTemplate}>
              <SelectTrigger aria-label="Rechtevorlage auswählen">
                <SelectValue placeholder="Rechtevorlage auswählen" />
              </SelectTrigger>
              <SelectContent>
                {(templates.data ?? []).map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templates.data?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Legen Sie zuerst unter „Rechtevorlagen“ eine Vorlage an.
              </p>
            )}
            {currentTemplate && (
              <ChangeList title="Rollen übernehmen" roles={currentTemplate.roles} />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyTo(null)}>
                Abbrechen
              </Button>
              <Button
                disabled={!currentTemplate}
                onClick={() => {
                  if (currentTemplate) {
                    setPersonRoles(applyTo, [
                      ...new Set([
                        ...(drafts[applyTo.id]?.after ?? directRoles(applyTo)),
                        ...currentTemplate.roles,
                      ]),
                    ])
                    setApplyTo(null)
                  }
                }}
              >
                In Entwurf übernehmen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {addPerson && (
        <Dialog open onOpenChange={setAddPerson}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Person hinzufügen</DialogTitle>
              <DialogDescription>
                Suchen Sie eine Person im Firmenverzeichnis. Anschließend können Sie eine
                Rechtevorlage übernehmen.
              </DialogDescription>
            </DialogHeader>
            <PersonPicker
              onSelect={(item) => {
                setAddPerson(false)
                setTab('people')
                setEditing(true)
                setApplyTo(item)
                setChosenTemplate('')
              }}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddPerson(false)}>
                Abbrechen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {newTemplateName !== null && (
        <Dialog open onOpenChange={(open) => !open && setNewTemplateName(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rechtevorlage anlegen</DialogTitle>
              <DialogDescription>
                Nach dem Anlegen wählen Sie die Modulrollen direkt in der Matrix aus.
              </DialogDescription>
            </DialogHeader>
            <Label htmlFor="template-name">Vorlagenname</Label>
            <Input
              id="template-name"
              maxLength={100}
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Zum Beispiel Buchhaltung"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewTemplateName(null)}>
                Abbrechen
              </Button>
              <Button
                disabled={!newTemplateName.trim()}
                onClick={() => {
                  const id = 'new:' + crypto.randomUUID()
                  setTemplateDrafts((old) => ({
                    ...old,
                    [id]: {
                      id,
                      name: newTemplateName.trim(),
                      version: EMPTY_VERSION,
                      roles: [],
                      isNew: true,
                    },
                  }))
                  setNewTemplateName(null)
                  setEditing(true)
                  setTemplateSearch('')
                }}
              >
                In Matrix anlegen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {templateDetails && (
        <TemplateDetails
          template={templateDetails}
          catalog={roles}
          editing={editing}
          onClose={() => setTemplateDetails(null)}
          onChange={(item) => {
            setTemplateDrafts((old) => {
              const next = { ...old, [item.id]: item }
              if (item.isNew && item.delete) delete next[item.id]
              return next
            })
            setTemplateDetails(null)
          }}
        />
      )}
      {information && (
        <Dialog open onOpenChange={(open) => !open && setInformation(null)}>
          <DialogContent className="flex max-h-[90svh] flex-col overflow-hidden sm:max-w-5xl">
            <DialogHeader>
              <DialogTitle>
                {information === 'catalog' ? 'Rollenkatalog' : 'Änderungsprotokoll'}
              </DialogTitle>
              <DialogDescription>
                Zusätzliche Informationen zur Berechtigungsverwaltung.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 overflow-y-auto">
              {information === 'catalog' ? <RollenTab /> : <AuditLogTab />}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInformation(null)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function EditBar({
  count,
  pending,
  onCancel,
  onReview,
}: {
  count: number
  pending: boolean
  onCancel: () => void
  onReview: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3">
      <span className="mr-auto text-sm" role="status">
        {count === 0
          ? 'Bearbeitungsmodus'
          : `${count} ${count === 1 ? 'Zeile geändert' : 'Zeilen geändert'}`}
      </span>
      <Button variant="outline" size="sm" disabled={pending} onClick={onCancel}>
        Abbrechen
      </Button>
      <Button size="sm" disabled={!count || pending} onClick={onReview}>
        Änderungen prüfen
      </Button>
    </div>
  )
}

function PermissionMatrix({
  label,
  firstColumn,
  rows,
  modules,
  catalog,
  editing,
  onChange,
  onRow,
  onApply,
}: {
  label: string
  firstColumn: string
  rows: MatrixRow[]
  modules: string[]
  catalog: VerwaltungsRolle[]
  editing: boolean
  onChange: (id: string, roles: string[]) => void
  onRow: (id: string) => void
  onApply?: (id: string) => void
}) {
  if (!rows.length)
    return (
      <EmptyState
        title="Keine Treffer"
        description="Passen Sie die Suche oder die Filter an."
      />
    )
  return (
    <>
      <div className="hidden rounded-lg border md:block [&_[data-slot=table-container]]:max-h-[65svh] [&_[data-slot=table-container]]:overflow-auto">
        <Table>
          <TableCaption className="sr-only">{label}</TableCaption>
          <TableHeader className="sticky top-0 z-20 bg-background">
            <TableRow>
              <TableHead
                scope="col"
                className="sticky left-0 z-30 min-w-56 bg-background align-bottom pb-2"
              >
                {firstColumn}
              </TableHead>
              {modules.map((id) => (
                <TableHead
                  key={id}
                  scope="col"
                  className="h-36 w-16 min-w-16 max-w-16 px-2 pb-2 align-bottom text-center"
                >
                  <span className="inline-block rotate-180 text-xs [writing-mode:vertical-rl]">
                    {moduleTitle(id)}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell scope="row" className="sticky left-0 z-10 bg-background py-0">
                  <RowName row={row} onRow={onRow} onApply={onApply} />
                </TableCell>
                {modules.map((id) => (
                  <TableCell key={id} className="px-1 py-0 text-center">
                    <MatrixCell
                      row={row}
                      moduleId={id}
                      catalog={catalog}
                      editing={editing}
                      onChange={(roles) => onChange(row.id, roles)}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ul
        aria-label={label + ' · kompakte Ansicht'}
        className="divide-y rounded-lg border md:hidden"
      >
        {rows.map((row) => (
          <li key={row.id} className="space-y-1 px-3 py-2">
            <RowName row={row} onRow={onRow} onApply={onApply} />
            <div className="space-y-0.5">
              {modules
                .filter(
                  (id) =>
                    editing ||
                    catalog.some(
                      (role) =>
                        role.moduleId === id &&
                        (row.roles.includes(role.name) ||
                          row.inherited.includes(role.name)),
                    ),
                )
                .map((id) => (
                  <div key={id} className="flex items-center justify-between gap-3">
                    <span className="text-sm">{moduleTitle(id)}</span>
                    <MatrixCell
                      row={row}
                      moduleId={id}
                      catalog={catalog}
                      editing={editing}
                      onChange={(roles) => onChange(row.id, roles)}
                    />
                  </div>
                ))}
              {!editing && row.roles.length + row.inherited.length === 0 && (
                <p className="text-sm text-muted-foreground">Keine Modulrechte</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}
function RowName({
  row,
  onRow,
  onApply,
}: {
  row: MatrixRow
  onRow: (id: string) => void
  onApply?: (id: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 md:flex-nowrap">
      <Button
        variant="link"
        className="h-auto p-0 font-medium leading-tight"
        title={row.email}
        onClick={() => onRow(row.id)}
      >
        {row.name}
      </Button>
      {row.email && (
        <span className="w-full text-xs text-muted-foreground md:sr-only">
          {row.email}
        </span>
      )}
      {row.blocked && <StatusBadge tone="warning">Gesperrt</StatusBadge>}
      {row.changed && <StatusBadge tone="info">Entwurf</StatusBadge>}
      {onApply && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={() => onApply(row.id)}
        >
          Vorlage anwenden
        </Button>
      )}
    </div>
  )
}
function MatrixCell({
  row,
  moduleId,
  catalog,
  editing,
  onChange,
}: {
  row: MatrixRow
  moduleId: string
  catalog: VerwaltungsRolle[]
  editing: boolean
  onChange: (roles: string[]) => void
}) {
  const options = catalog.filter((role) => role.moduleId === moduleId)
  const effective = [...new Set([...row.roles, ...row.inherited])].filter((name) =>
    options.some((role) => role.name === name),
  )
  const title = effective.length ? effective.map(roleTitle).join(', ') : 'Kein Zugriff'
  const admin = effective.some((name) => name.endsWith('.admin'))
  const additional = effective.some(
    (name) => !name.endsWith('.admin') && !name.endsWith('.nutzer'),
  )
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 min-w-10 gap-1 px-1 py-0"
          aria-label={`${row.name} · ${moduleTitle(moduleId)}: ${title}${editing ? ' · Rollen bearbeiten' : ''}`}
          title={title}
        >
          {effective.length ? (
            <>
              <span
                aria-hidden="true"
                className="flex size-4 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground"
              >
                <Check className="size-3" />
              </span>
              <span className="text-xs font-semibold">
                {admin ? 'A' : ''}
                {additional ? '+' : ''}
              </span>
            </>
          ) : (
            <span aria-hidden="true" className="size-4 rounded-sm border border-input" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center">
        <div className="space-y-3">
          <div>
            <p className="font-medium">{moduleTitle(moduleId)}</p>
            <p className="text-xs text-muted-foreground">{row.name}</p>
          </div>
          {options.map((role) => (
            <div key={role.name} className="flex items-start gap-2">
              {editing ? (
                <Checkbox
                  id={`${row.id}-${role.name}`}
                  checked={row.roles.includes(role.name)}
                  onCheckedChange={(checked) =>
                    onChange(
                      checked
                        ? [...row.roles, role.name]
                        : row.roles.filter((name) => name !== role.name),
                    )
                  }
                  aria-label={roleTitle(role.name) + ' · ' + role.description}
                />
              ) : effective.includes(role.name) ? (
                <Check className="size-4 shrink-0" />
              ) : (
                <span className="size-4 shrink-0" />
              )}
              <div>
                <Label
                  htmlFor={editing ? `${row.id}-${role.name}` : undefined}
                  className="text-sm"
                >
                  {roleTitle(role.name)}
                </Label>
                <p className="text-xs text-muted-foreground">{role.description}</p>
                {row.inherited.includes(role.name) && (
                  <p className="text-xs text-muted-foreground">
                    Zusätzlich aus dem Verzeichnis; hier nicht entziehbar.
                  </p>
                )}
              </div>
            </div>
          ))}
          {!editing && (
            <p className="text-xs text-muted-foreground">
              Zum Ändern wählen Sie „
              {row.email ? 'Rechte bearbeiten' : 'Vorlagen bearbeiten'}“.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
function ChangeList({ title, roles }: { title: string; roles: string[] }) {
  return (
    <div className="mt-2 text-sm">
      <p className="font-medium">{title}</p>
      <ul className="text-muted-foreground">
        {roles.length ? (
          roles.map((role) => (
            <li key={role}>
              {moduleTitle(role.split('.')[0]!)} · {roleTitle(role)}
            </li>
          ))
        ) : (
          <li>Keine</li>
        )}
      </ul>
    </div>
  )
}
function TemplateDetails({
  template,
  catalog,
  editing,
  onClose,
  onChange,
}: {
  template: TemplateDraft
  catalog: VerwaltungsRolle[]
  editing: boolean
  onClose: () => void
  onChange: (item: TemplateDraft) => void
}) {
  const [name, setName] = useState(template.name)
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90svh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Rechtevorlage</DialogTitle>
          <DialogDescription>
            Änderungen wirken sich nicht auf bereits vergebene Personenrechte aus.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 space-y-3 overflow-y-auto">
          {editing ? (
            <>
              <Label htmlFor="edit-template-name">Vorlagenname</Label>
              <Input
                id="edit-template-name"
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </>
          ) : (
            <p className="font-medium">{name}</p>
          )}
          <ChangeList title="Enthaltene Rollen" roles={template.roles} />
          {!catalog.length && <p>Der Rollenkatalog wird geladen.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
          {editing && (
            <>
              <Button
                variant="destructive"
                onClick={() => onChange({ ...template, delete: true })}
              >
                Zum Löschen vormerken
              </Button>
              <Button
                disabled={!name.trim()}
                onClick={() => onChange({ ...template, name: name.trim() })}
              >
                In Entwurf übernehmen
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
